import { useState, useRef, useEffect, useMemo } from 'react';
import { Brain, Sparkles, RefreshCw, AlertCircle, Send, Download, Copy, Check, Zap, ChevronDown, ChevronUp, FileText, Target, TrendingUp, Shield, MessageSquare, ClipboardList, Lightbulb, NotebookPen } from '../icons';
import { MarkdownRenderer } from './MarkdownRenderer';
import { BREAKTHROUGH_QUICK_TOPICS, BREAKTHROUGH_REPORT_PROMPT } from './breakthroughConstants';
import { buildBreakthroughPrompt } from './buildBreakthroughPrompt';
import { supabase } from '../../lib/supabase';
import type { Score } from '../../types';
import type { HarmonicScore } from '../../types/harmonic';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface BreakthroughReportPanelProps {
  analysis: string | undefined;
  scores: Score[];
  harmonicScores: HarmonicScore[];
  resultId: string;
  respondentName?: string;
  respondentEmail?: string;
  completedAt?: Date;
  onAnalysisUpdate: (analysis: string) => void;
  onBack: () => void;
}

const CONTINUATION_MARKER = 'Continue generating Part';
const MAX_CONTINUATIONS = 4;

interface ReportSection {
  id: string;
  label: string;
  icon: (props: { className?: string }) => JSX.Element;
  keywords: string[];
}

const REPORT_SECTIONS: ReportSection[] = [
  { id: 'full', label: 'Full Report', icon: FileText, keywords: [] },
  { id: 'overview', label: 'Breakthrough Overview', icon: Brain, keywords: ['breakthrough overview', 'overview'] },
  { id: 'critical', label: 'Critical Breakthrough Zone', icon: AlertCircle, keywords: ['critical breakthrough zone'] },
  { id: 'development', label: 'Development Zone', icon: Target, keywords: ['development breakthrough zone'] },
  { id: 'leverage', label: 'Leverage Zone', icon: TrendingUp, keywords: ['leverage zone'] },
  { id: 'blocker', label: 'Blocker–Trait Interaction', icon: Shield, keywords: ['blocker–trait interaction', 'blocker-trait interaction'] },
  { id: 'beliefs', label: 'Beliefs & Emotions', icon: MessageSquare, keywords: ['likely beliefs', 'emotional patterns'] },
  { id: 'strategy', label: 'Coaching Strategy', icon: ClipboardList, keywords: ['coaching strategy', 'action plan'] },
  { id: 'notes', label: 'Coach Notes', icon: NotebookPen, keywords: ['coach notes'] },
];

function extractSectionContent(fullText: string, section: ReportSection): string {
  if (section.id === 'full') return fullText;
  const lines = fullText.split('\n');
  const lowerKeywords = section.keywords.map(k => k.toLowerCase());
  let startIndex = -1;
  let endIndex = lines.length;

  for (let i = 0; i < lines.length; i++) {
    const lowerLine = lines[i].toLowerCase();
    if (startIndex === -1) {
      if (lowerKeywords.some(kw => lowerLine.includes(kw))) {
        startIndex = i;
      }
    } else {
      const isNextSectionHeader = REPORT_SECTIONS.some(s => {
        if (s.id === section.id || s.id === 'full') return false;
        return s.keywords.some(kw => lowerLine.includes(kw.toLowerCase()));
      });
      if (isNextSectionHeader) {
        endIndex = i;
        break;
      }
    }
  }

  if (startIndex === -1) return fullText;
  return lines.slice(startIndex, endIndex).join('\n').trim();
}

export function BreakthroughReportPanel({
  analysis,
  scores,
  harmonicScores,
  resultId,
  respondentName,
  respondentEmail,
  completedAt,
  onAnalysisUpdate,
  onBack,
}: BreakthroughReportPanelProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [copied, setCopied] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('full');
  const [showExplore, setShowExplore] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const analysisRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (analysis && messages.length === 0) {
      setMessages([{
        id: 'initial-analysis',
        role: 'assistant',
        content: analysis,
        timestamp: new Date(),
      }]);
    }
  }, [analysis]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fullReportText = useMemo(() => {
    return messages.filter(m => m.role === 'assistant').map(m => m.content).join('\n\n');
  }, [messages]);

  const displayedContent = useMemo(() => {
    if (!fullReportText) return '';
    const section = REPORT_SECTIONS.find(s => s.id === activeSection);
    if (!section) return fullReportText;
    return extractSectionContent(fullReportText, section);
  }, [fullReportText, activeSection]);

  const getUserId = async (): Promise<string | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  };

  const callGenerateAnalysis = async (
    prompt: string,
    conversationHistory?: { role: string; content: string }[],
    userId?: string | null
  ) => {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        prompt,
        conversation_history: conversationHistory,
        result_id: resultId,
        user_id: userId,
        analysis_type: 'breakthrough',
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to generate breakthrough report');
    }

    return response.json();
  };

  const needsContinuation = (text: string): boolean => {
    const lastLines = text.slice(-200).toLowerCase();
    return lastLines.includes('continue generating part') ||
           lastLines.includes('continued in part') ||
           lastLines.includes('end of part');
  };

  const saveAnalysis = async (content: string) => {
    const { error: updateError } = await supabase
      .from('results')
      .update({
        breakthrough_analysis: content,
        breakthrough_analysis_generated_at: new Date().toISOString()
      })
      .eq('id', resultId);

    if (updateError) {
      console.error('Failed to save breakthrough analysis:', updateError.message);
    }
  };

  const sendFullReport = async (instruction: string, isInitialGeneration: boolean) => {
    const prompt = buildBreakthroughPrompt(scores, harmonicScores, instruction);
    setIsGenerating(true);
    setAiError(null);
    setGenerationProgress('Generating breakthrough report (Part 1)...');

    try {
      const userId = await getUserId();
      let fullContent = '';
      let continuationCount = 0;
      let conversationHistory: { role: string; content: string }[] = [];

      const firstData = await callGenerateAnalysis(prompt, undefined, userId);
      fullContent = firstData.analysis;
      conversationHistory = [
        { role: 'user', content: prompt },
        { role: 'assistant', content: fullContent },
      ];

      while (needsContinuation(fullContent) && continuationCount < MAX_CONTINUATIONS) {
        continuationCount++;
        setGenerationProgress(`Generating breakthrough report (Part ${continuationCount + 1})...`);

        const continuationPrompt = buildBreakthroughPrompt(
          scores,
          harmonicScores,
          `Continue the breakthrough report from where you left off. Continue writing the remaining sections. Do not repeat content already written. Maintain the same depth and quality. If there are more sections remaining after this, end with "Continue generating Part ${continuationCount + 2}." If this is the final part, write the conclusion and do not add a continuation marker.`
        );

        const contData = await callGenerateAnalysis(
          continuationPrompt,
          conversationHistory,
          userId
        );

        fullContent += '\n\n' + contData.analysis;
        conversationHistory.push(
          { role: 'user', content: continuationPrompt },
          { role: 'assistant', content: contData.analysis },
        );
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: fullContent,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (isInitialGeneration) {
        await saveAnalysis(fullContent);
        onAnalysisUpdate(fullContent);
      }
    } catch (err) {
      console.error('Error generating breakthrough report:', err);
      setAiError((err as Error).message || 'Failed to generate breakthrough report. Please try again.');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(null);
    }
  };

  const sendMessage = async (instruction: string, isInitialGeneration = false) => {
    const prompt = buildBreakthroughPrompt(scores, harmonicScores, instruction);

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: instruction,
      timestamp: new Date(),
    };

    if (!isInitialGeneration) {
      setMessages(prev => [...prev, userMessage]);
    }

    setIsGenerating(true);
    setAiError(null);

    try {
      const userId = await getUserId();

      const conversationHistory = messages
        .filter(m => m.id !== 'initial-analysis')
        .map(m => ({ role: m.role, content: m.content }));

      const data = await callGenerateAnalysis(
        prompt,
        isInitialGeneration ? undefined : conversationHistory,
        userId
      );

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.analysis,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      if (isInitialGeneration) {
        await saveAnalysis(data.analysis);
        onAnalysisUpdate(data.analysis);
      }
    } catch (err) {
      console.error('Error generating breakthrough analysis:', err);
      setAiError((err as Error).message || 'Failed to generate breakthrough analysis. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleQuickTopic = (topic: typeof BREAKTHROUGH_QUICK_TOPICS[0]) => {
    const isFullReport = 'isFullReport' in topic && topic.isFullReport;
    if (isFullReport) {
      sendFullReport(topic.prompt, messages.length === 0);
    } else {
      sendMessage(topic.prompt, messages.length === 0);
    }
  };

  const handleChatSubmit = () => {
    if (!chatInput.trim() || isGenerating) return;
    const instruction = chatInput.trim();
    setChatInput('');
    sendMessage(instruction, messages.length === 0);
  };

  const handleRegenerate = () => {
    setMessages([]);
    setActiveSection('full');
    sendFullReport(BREAKTHROUGH_REPORT_PROMPT, true);
  };

  const handleCopy = async () => {
    if (!fullReportText) return;
    await navigator.clipboard.writeText(fullReportText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportPDF = async () => {
    if (!analysisRef.current) return;
    const html2pdf = (await import('html2pdf.js')).default;
    html2pdf()
      .set({
        margin: [15, 15, 15, 15],
        filename: `NLP-OS-Breakthrough-Report${respondentName ? `-${respondentName}` : ''}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      })
      .from(analysisRef.current)
      .save();
  };

  const hasMessages = messages.length > 0;
  const hasAssistantMessage = messages.some(m => m.role === 'assistant');

  return (
    <div className="min-h-screen freud-bg">
      {/* Respondent Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-chocolate-500 hover:bg-cream-100 hover:text-chocolate-700 transition-colors"
              >
                <ChevronUp className="w-4 h-4 rotate-[-90deg]" />
                Back to Respondents
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-coral-100 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-coral-600" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">{respondentName || 'Respondent'}</h1>
                <div className="flex items-center gap-3 text-sm text-chocolate-500">
                  {respondentEmail && <span>{respondentEmail}</span>}
                  {completedAt && (
                    <>
                      <span className="text-chocolate-300">·</span>
                      <span>Assessed: {completedAt.toLocaleDateString()}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasAssistantMessage && !isGenerating && (
                <button
                  onClick={handleRegenerate}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-coral-600 text-white rounded-xl text-sm font-semibold hover:bg-coral-700 transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  New Report
                </button>
              )}
              {hasAssistantMessage && (
                <>
                  <button
                    onClick={handleCopy}
                    disabled={isGenerating}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-cream-100 text-chocolate-600 rounded-xl text-sm font-medium hover:bg-cream-200 transition-all disabled:opacity-40"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    onClick={handleExportPDF}
                    disabled={isGenerating}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-chocolate-700 text-white rounded-xl text-sm font-medium hover:bg-chocolate-800 transition-all disabled:opacity-40"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {aiError && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{aiError}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-[240px_1fr] gap-6">
          {/* Left-hand Report Navigation */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="freud-card-elevated p-4">
              <div className="flex items-center gap-2 mb-3 px-2">
                <Brain className="w-4 h-4 text-coral-600" />
                <h3 className="text-xs font-bold text-chocolate-900 uppercase tracking-wider">Report Sections</h3>
              </div>
              <nav className="flex flex-col gap-1">
                {REPORT_SECTIONS.map((section) => {
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-left ${
                        isActive
                          ? 'bg-coral-50 text-coral-700 border-l-2 border-coral-500'
                          : 'text-chocolate-600 hover:bg-cream-50 hover:text-chocolate-800'
                      }`}
                    >
                      <section.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-coral-600' : 'text-chocolate-400'}`} />
                      <span className="truncate">{section.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Report Area */}
          <div className="min-w-0">
            <div ref={analysisRef} className="freud-card-elevated overflow-hidden">
              {!hasMessages && !isGenerating ? (
                <div className="py-20 px-8 text-center">
                  <div className="w-16 h-16 bg-coral-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <Zap className="w-8 h-8 text-coral-400" />
                  </div>
                  <h2 className="freud-heading-xl text-chocolate-900 mb-3">NLP OS™ Breakthrough Report</h2>
                  <p className="text-chocolate-500 mb-2 max-w-lg mx-auto leading-relaxed">
                    Generate a focused breakthrough report that identifies the highest-leverage zones for change —
                    combining the Characteristic Profile and Harmonic Scale.
                  </p>
                  <p className="text-sm text-chocolate-400 mb-8 max-w-lg mx-auto">
                    Treat all interpretations as coaching hypotheses to explore, not fixed labels or diagnoses.
                  </p>
                  <button
                    onClick={() => sendFullReport(BREAKTHROUGH_REPORT_PROMPT, true)}
                    className="freud-btn-primary inline-flex items-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    Generate Breakthrough Report
                  </button>
                </div>
              ) : (
                <div className="px-8 py-6">
                  {activeSection !== 'full' && (
                    <div className="mb-4 pb-3 border-b border-cream-200">
                      <h2 className="text-lg font-bold text-chocolate-900">
                        {REPORT_SECTIONS.find(s => s.id === activeSection)?.label}
                      </h2>
                    </div>
                  )}
                  {displayedContent ? (
                    <MarkdownRenderer content={displayedContent} />
                  ) : (
                    <p className="text-chocolate-400 text-sm italic py-8 text-center">
                      This section was not found in the report. Try generating the full report first.
                    </p>
                  )}

                  {isGenerating && (
                    <div className="flex justify-start mt-4">
                      <div className="bg-cream-50 border border-cream-200 rounded-2xl px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 bg-coral-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-2 h-2 bg-coral-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-2 h-2 bg-coral-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                          {generationProgress && (
                            <span className="text-sm text-chocolate-500">{generationProgress}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Explore Further - Collapsible */}
            {hasAssistantMessage && (
              <div className="mt-6 freud-card-elevated overflow-hidden">
                <button
                  onClick={() => setShowExplore(!showExplore)}
                  className="w-full flex items-center justify-between px-6 py-4 hover:bg-cream-50 transition-colors"
                >
                  <div className="flex items-center gap-2.5">
                    <Lightbulb className="w-5 h-5 text-coral-600" />
                    <h3 className="text-sm font-bold text-chocolate-900 uppercase tracking-wide">Explore Further</h3>
                  </div>
                  {showExplore ? <ChevronUp className="w-5 h-5 text-chocolate-400" /> : <ChevronDown className="w-5 h-5 text-chocolate-400" />}
                </button>

                {showExplore && (
                  <div className="px-6 pb-6 border-t border-cream-200">
                    <p className="text-sm text-chocolate-500 mt-4 mb-4">
                      Use these focused coaching lenses to dive deeper into specific areas of the breakthrough report.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
                      {BREAKTHROUGH_QUICK_TOPICS.filter(t => !('isFullReport' in t && t.isFullReport)).map((topic) => (
                        <button
                          key={topic.label}
                          onClick={() => handleQuickTopic(topic)}
                          disabled={isGenerating}
                          className="px-4 py-2.5 rounded-xl text-sm font-semibold text-left bg-coral-50 text-coral-700 hover:bg-coral-100 transition-all disabled:opacity-50"
                        >
                          {topic.label}
                        </button>
                      ))}
                    </div>

                    {/* Follow-up conversation */}
                    <div className="border-t border-cream-200 pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="w-4 h-4 text-coral-600" />
                        <h4 className="text-sm font-bold text-chocolate-900">Ask a Follow-up Question</h4>
                      </div>
                      <div className="flex items-center gap-3">
                        <input
                          type="text"
                          placeholder={
                            respondentName
                              ? `Ask about ${respondentName}'s breakthrough zones...`
                              : 'Ask a follow-up question...'
                          }
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleChatSubmit()}
                          disabled={isGenerating}
                          className="freud-input"
                        />
                        <button
                          onClick={handleChatSubmit}
                          disabled={isGenerating || !chatInput.trim()}
                          className="p-3 bg-coral-600 text-white rounded-xl hover:bg-coral-700 transition-all disabled:opacity-50 flex-shrink-0"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
